// SOURCE OF TRUTH: sync renderer.ts across bynova-product-studio,
// bynova-storefront-widget, bynova-workers when modifying.

import type opentype from "opentype.js";
import { FabricObject, type TMat2D } from "fabric";
import Layer from "../Layer";
import { FabricIText, FabricPath, FabricPattern, util } from "../../objects";
import { WarpedTextProps } from "./interfaces";
import { ObjectFitType } from "../../interfaces";
import {
  EnvelopeMapper,
  fitObjectPattern,
  GlyphPathCommand,
  parseTransformOptions,
  toTitleCase,
  warpPathCommands,
} from "../../utils";

export abstract class WarpedTextRenderer extends Layer {
  protected textObject!: FabricIText;
  protected _opentypeFont?: opentype.Font;
  protected patternObject?: FabricPattern;

  protected getGlyphsToRemove(): FabricObject[] {
    return [...this._objects];
  }

  protected autoFitFontSize(font: opentype.Font, text: string): number {
    const fontSizeScale = this.textObject.fontSizeScalable
      ? Math.min(this.scaleX, this.scaleY)
      : 1;
    const maxFontSize = this.textObject.maxFontSize * fontSizeScale;

    let low = 0;
    let high = maxFontSize;
    let best = 1;

    while (high - low > 0.1) {
      const mid = (low + high) / 2;
      if (font.getAdvanceWidth(text, mid) <= this.width) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    return best;
  }

  protected renderGlyphs(): void {
    for (const obj of this.getGlyphsToRemove()) {
      this.remove(obj);
    }

    const font = this._opentypeFont;
    const { text, fill, stroke, strokeWidth, strokeLineJoin, paintFirst } = this.textObject;
    const transformOptions = parseTransformOptions(this.textObject.transformOptions);

    if (!font || !text || !transformOptions) return;

    const fontSize = this.autoFitFontSize(font, text);

    const advanceWidth = font.getAdvanceWidth(text, fontSize);
    if (advanceWidth <= 0) return;

    const glyphPaths = font.getPaths(text, 0, 0, fontSize);

    // Compute actual ink bounds (excludes side bearings and descender space)
    let inkMinX = Infinity;
    let inkMaxX = -Infinity;
    let inkMinY = Infinity;
    let inkMaxY = -Infinity;
    for (const gp of glyphPaths) {
      const bb = gp.getBoundingBox();
      if (bb.x1 === bb.x2 && bb.y1 === bb.y2) continue;
      inkMinX = Math.min(inkMinX, bb.x1);
      inkMaxX = Math.max(inkMaxX, bb.x2);
      inkMinY = Math.min(inkMinY, bb.y1);
      inkMaxY = Math.max(inkMaxY, bb.y2);
    }
    if (!isFinite(inkMinX) || !isFinite(inkMinY)) return;

    const inkWidth = inkMaxX - inkMinX;
    const inkHeight = inkMaxY - inkMinY;
    if (inkWidth <= 0 || inkHeight <= 0) return;

    // FabricPath dimensions come from fill-only path bounds; stroke is painted
    // half outside those bounds, so reserve strokeWidth total before warping.
    const padding = strokeWidth;
    const effectiveWidth  = Math.max(1, this.width  - padding);
    const effectiveHeight = Math.max(1, this.height - padding);

    const scaleUpH = effectiveWidth / inkWidth;
    const scaleUpV = effectiveHeight / inkHeight;

    const mapper = new EnvelopeMapper(effectiveWidth, 0, effectiveHeight, transformOptions);
    const paths: FabricPath[] = [];

    for (const glyphPath of glyphPaths) {
      const bbox = glyphPath.getBoundingBox();
      // Skip glyphs with no outline (spaces, combining marks with no contour)
      if (bbox.x1 === bbox.x2 && bbox.y1 === bbox.y2) continue;

      const warpedSvg = warpPathCommands(
        glyphPath.commands as GlyphPathCommand[],
        // Offset ink to origin, scale independently per axis to fill layer bounds
        (px, py) => mapper.mapPoint((px - inkMinX) * scaleUpH, (py - inkMinY) * scaleUpV),
      );

      paths.push(
        new FabricPath(warpedSvg, {
          fill,
          stroke,
          strokeWidth,
          strokeLineJoin,
          paintFirst,
          fillRule: "evenodd",
          objectCaching: false,
        }),
      );
    }

    if (paths.length > 0) {
      this.add(...paths);
      for (const path of paths) {
        // Group.add() bakes the inverse group transform into each child via enterGroup(obj, true).
        // Paths are already in local coordinates, so reset to identity before repositioning.
        path.set({ angle: 0, scaleX: 1, scaleY: 1, skewX: 0, skewY: 0 });
        path.setPositionByOrigin(path.pathOffset, "center", "center");
        path.setCoords();
      }
      this.applyGlyphPattern(paths, fontSize);
    }
  }

  // Each glyph is a separate fabric.Path, so a naive shared Pattern fill would
  // restart at every glyph's own local origin (chopped-per-letter look).
  // Instead, give every glyph its own Pattern instance whose patternTransform
  // is the shared "global" pattern matrix (F) composed with the inverse of
  // that glyph's own placement matrix — this keeps the pattern anchored to
  // the whole warped word instead of to each individual glyph. offsetX/offsetY
  // cancel the -width/2,-height/2 origin fabric applies per-object when
  // painting a pattern fill (verified against fabric v6 Object/Pattern source).
  // F itself must also carry a -layerWidth/2,-layerHeight/2 translation —
  // that's the offset fabric would normally apply automatically for a single
  // whole-object fill (using the object's own width/height); since we cancel
  // that per-glyph offset above, we have to bake the layer-level version of
  // it into F so the pattern's own top-left corner anchors to the layer's
  // top-left corner instead of the layer's center (verified empirically).
  protected applyGlyphPattern(paths: FabricPath[], fontSize: number): void {
    if (!this.patternObject) return;

    const patternSrc = this.patternObject.source as HTMLImageElement;
    if (!patternSrc) return;

    const patternScale = this.textObject.patternScale || 1;
    const patternFit = this.textObject.patternFit ?? "cover";

    let scale: number;
    if (patternFit === "cover") {
      fitObjectPattern(this.patternObject, this, ObjectFitType.Cover, patternScale);
      scale = this.patternObject.patternTransform?.[0] ?? 1;
    } else {
      const { height: imgHeight } = patternSrc;
      if (!imgHeight) return;
      scale = (fontSize / imgHeight) * patternScale;
    }
    const globalTransform: TMat2D = [scale, 0, 0, scale, -this.width / 2, -this.height / 2];

    for (const path of paths) {
      const ownMatrix = path.calcOwnMatrix();
      const patternTransform = util.multiplyTransformMatrices(
        util.invertTransform(ownMatrix),
        globalTransform,
      );
      path.set({
        fill: new FabricPattern({
          source: patternSrc,
          crossOrigin: "anonymous",
          repeat: "repeat",
          offsetX: path.width / 2,
          offsetY: path.height / 2,
          patternTransform,
        }),
      });
    }
  }

  protected updateText(): void {
    const {
      prefix = "",
      infix = "",
      suffix = "",
      textCase = "",
    } = this.textObject;

    let text = infix ? `${prefix}${infix}${suffix}` : "";
    if (textCase === "lowercase") {
      text = text.toLowerCase();
    } else if (textCase === "title-case") {
      text = toTitleCase(text);
    } else if (textCase === "uppercase") {
      text = text.toUpperCase();
    }

    this.textObject.set({ text });
  }

  setTextProps(props: Partial<WarpedTextProps>): void {
    this.textObject.set(props);
    this.rerender();
  }
}
